import { Injectable } from '@angular/core';

export type RingtoneType = 'ringing' | 'outgoing' | 'callee-incoming';

@Injectable({ providedIn: 'root' })
export class CallAudioService {

  // Audio instances
  private calleeIncoming: HTMLAudioElement | null = null;
  private ringing: HTMLAudioElement | null = null;
  private outgoing: HTMLAudioElement | null = null;

  private soundEnabled = false;
  private currentlyPlaying: RingtoneType | null = null;

  // Sync audio giữa các tab
  private channel = new BroadcastChannel('call-audio');

  private audioUnlocked = false;

  constructor() {
    this.channel.onmessage = (e) => {
      if (e.data === 'STOP_ALL_AUDIO') {
        this.stop(false);
      }
    };
  }

  /**
   * Gọi 1 lần sau user interaction để unlock autoplay
   */
  enableSound(): void {
    if (this.soundEnabled) return;

    this.initializeAudio();
    const test = this.ringing;
    if (!test) return;
    test.volume = 0;

    test.play()
      .then(() => {
        test.pause();
        test.currentTime = 0;
        test.volume = 1;
        this.soundEnabled = true;
      })
      .catch(err => {
        console.warn('Enable sound failed:', err);
      })
      .finally(() => {
        this.resetAudio();
      });
  }

  /**
   * PLAY – chỉ khi type khác hiện tại
   */
  play(type: RingtoneType): void {
    if (!this.soundEnabled) return;

    if (this.currentlyPlaying === type) return;

    this.channel.postMessage('STOP_ALL_AUDIO');
    this.stop(false);

    this.initializeAudio();
    const audio = this.getAudio(type)!;
    audio.currentTime = 0;

    audio.play()
      .then(() => {
        this.currentlyPlaying = type;
      })
      .catch(err => {
        console.warn(`Cannot play ${type}:`, err);
      });
  }

  /**
   * STOP – idempotent
   */
  stop(broadcast = true): void {
    if (this.currentlyPlaying === null) return;

    [this.calleeIncoming, this.ringing, this.outgoing].forEach(a => {
      if (!a) return;
      a.pause();
      a.currentTime = 0;
    });

    this.currentlyPlaying = null;
    this.resetAudio();

    if (broadcast) {
      this.channel.postMessage('STOP_ALL_AUDIO');
    }
  }

  private getAudio(type: RingtoneType): HTMLAudioElement | null {
    switch (type) {
      case 'callee-incoming':
        return this.calleeIncoming;
      case 'ringing':
        return this.ringing;
      case 'outgoing':
        return this.outgoing;
    }
  }

  initializeAudio() {
    this.resetAudio();
    this.calleeIncoming = new Audio('/assets/audio/callee_incoming.mp3');
    this.calleeIncoming.loop = true;
    this.calleeIncoming.preload = 'auto';
    this.ringing = new Audio('/assets/audio/ringing.mp3');
    this.ringing.loop = true;
    this.ringing.preload = 'auto';
    this.outgoing = new Audio('/assets/audio/outgoing.mp3');
    this.outgoing.loop = true;
    this.outgoing.preload = 'auto';
  }

  resetAudio() {
    this.calleeIncoming?.pause();
    this.calleeIncoming = null;
    this.ringing?.pause();
    this.ringing = null;
    this.outgoing?.pause();
    this.outgoing = null;
  }

  unlockAudio(): void {
    if (this.audioUnlocked) return;

    // Tạo silent buffer và play ngay khi user tap/click lần đầu
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioContext();
    const buffer = ctx.createBuffer(1, 1, 22050);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);

    this.audioUnlocked = true;
  }
}
